import { isRight } from 'fp-ts/Either';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useAsyncData } from '../hooks/useAsyncData';
import { usePageTitle } from '../hooks/usePageTitle';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { useTRPC } from '../hooks/useTRPC';
import type { RightResult, RouterOutputs } from '../types/TRPC';

type Stats = RightResult<RouterOutputs['dappAnalytics']['getDappAnalytics']>;
type SuppressedValue = Stats['totals']['installs'];
type Range = Stats['range'];

const ranges: Range[] = ['7d', '30d', '90d'];

function displayValue(
  metric: SuppressedValue,
  formatter: (value: number) => string = (value) =>
    new Intl.NumberFormat().format(value),
): string {
  if (metric.suppressed) {
    return '< 10';
  }
  return metric.value === null ? 'Unavailable' : formatter(metric.value);
}

const formatPercent = (value: number) => `${value.toFixed(1)}%`;
const formatDuration = (value: number) => {
  const minutes = value / 60_000;
  return minutes >= 60
    ? `${(minutes / 60).toFixed(1)} h`
    : `${minutes.toFixed(1)} min`;
};

function MetricCard({
  label,
  metric,
  note,
  formatter,
}: {
  label: string;
  metric: SuppressedValue;
  note?: string;
  formatter?: (value: number) => string;
}) {
  return (
    <div className="rounded-xl border border-neutral-800 bg-neutral-950/70 p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold text-white">
        {displayValue(metric, formatter)}
      </div>
      {note && <div className="mt-1 text-xs text-neutral-500">{note}</div>}
    </div>
  );
}

function TrendBars({ stats }: { stats: Stats }) {
  const maximum = Math.max(
    1,
    ...stats.daily.flatMap((day) => [
      day.installs.value ?? 0,
      day.activeDevices.value ?? 0,
    ]),
  );

  return (
    <div
      className="flex h-48 items-end gap-1 overflow-x-auto border-b border-neutral-800 pb-1"
      aria-label="Daily installs and active devices trend"
    >
      {stats.daily.map((day) => (
        <div
          key={day.date}
          className="flex h-full min-w-4 flex-1 items-end gap-px"
          title={`${day.date}: installs ${displayValue(day.installs)}, active devices ${displayValue(day.activeDevices)}`}
        >
          <div
            className={`w-1/2 rounded-t bg-teal-400/80 ${day.provisional ? 'opacity-45' : ''}`}
            style={{
              height: `${Math.max(2, ((day.installs.value ?? 0) / maximum) * 100)}%`,
            }}
          />
          <div
            className={`w-1/2 rounded-t bg-violet-400/70 ${day.provisional ? 'opacity-45' : ''}`}
            style={{
              height: `${Math.max(2, ((day.activeDevices.value ?? 0) / maximum) * 100)}%`,
            }}
          />
        </div>
      ))}
    </div>
  );
}

function VersionShare({
  title,
  rows,
}: {
  title: string;
  rows: Stats['versionShare7d'];
}) {
  return (
    <section className="rounded-xl border border-neutral-800 bg-neutral-950/70 p-5">
      <h2 className="text-base font-semibold text-white">{title}</h2>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-neutral-500">Unavailable</p>
      ) : (
        <div className="mt-4 space-y-3">
          {rows.map((row) => (
            <div key={row.key}>
              <div className="flex items-center justify-between gap-3 text-sm">
                <span className="truncate text-neutral-300">
                  {row.versionName ?? `Version ${row.versionCode}`}
                </span>
                <span className="tabular-nums text-neutral-400">
                  {displayValue(row.devices)} ·{' '}
                  {displayValue(row.percentage, formatPercent)}
                </span>
              </div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-neutral-800">
                <div
                  className="h-full rounded-full bg-teal-400"
                  style={{
                    width: `${Math.min(100, row.percentage.value ?? 0)}%`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function DappStatsPage() {
  const { canRender } = useRequireAuth({ requireRole: 'superadmin' });
  const { dappId } = useParams<{ dappId: string }>();
  const trpc = useTRPC();
  const [overridePackage, setOverridePackage] = useState('');
  const [fixedRank, setFixedRank] = useState('');
  const [rankingMessage, setRankingMessage] = useState<string | null>(null);
  const [savingRank, setSavingRank] = useState(false);
  const {
    data: state,
    isLoading,
    error,
    run,
  } = useAsyncData<{ stats: Stats | null; range: Range }>({
    initialData: { stats: null, range: '30d' },
    initialLoading: true,
  });

  usePageTitle('Dapp Stats | Publishing Portal');

  const load = useCallback(
    async (range: Range) => {
      if (!dappId) {
        return;
      }
      await run(
        () =>
          trpc.dappAnalytics.getDappAnalytics.query({
            dappId,
            range,
          }),
        {
          onSuccess: (result, helpers) => {
            if (isRight(result)) {
              helpers.setData({ stats: result.right, range });
            } else {
              helpers.setError(result.left.message);
            }
          },
          onError: (_loadError, helpers) => {
            helpers.setError('Failed to load dapp analytics');
          },
        },
      );
    },
    [dappId, run, trpc],
  );

  useEffect(() => {
    if (canRender) {
      void load('30d');
    }
  }, [canRender, load]);

  useEffect(() => {
    setFixedRank(state.stats?.ranking?.fixedRank?.toString() ?? '');
  }, [state.stats?.ranking?.fixedRank]);

  useEffect(() => {
    setOverridePackage(state.stats?.dapp.androidPackage ?? '');
  }, [state.stats?.dapp.androidPackage]);

  const saveFixedRank = async (value: number | null) => {
    const stats = state.stats;
    if (!stats) return;
    setSavingRank(true);
    setRankingMessage(null);
    try {
      const result = await trpc.dappAnalytics.setDappRankingOverride.mutate({
        androidPackage: overridePackage,
        fixedRank: value,
      });
      if (isRight(result)) {
        setRankingMessage(
          value === null
            ? `Fixed rank cleared for ${overridePackage}.`
            : `${overridePackage} will use global rank ${value} in the next daily ranking.`,
        );
        setFixedRank(value?.toString() ?? '');
      } else {
        setRankingMessage(result.left.message);
      }
    } catch {
      setRankingMessage('Failed to update the fixed global rank.');
    } finally {
      setSavingRank(false);
    }
  };

  if (!canRender) {
    return null;
  }

  const stats = state.stats;
  return (
    <main className="min-h-screen bg-neutral-950 px-4 py-8 text-neutral-100 md:px-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="flex flex-col gap-4 border-b border-neutral-800 pb-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-teal-400">
              Internal · Seeker analytics
            </div>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight text-white">
              {stats?.dapp.name ?? 'Dapp stats'}
            </h1>
            <p className="mt-2 text-sm text-neutral-500">
              Store lifecycle and telemetry-consented usage. Days are UTC.
            </p>
          </div>
          <div className="flex rounded-lg border border-neutral-800 bg-neutral-900 p-1">
            {ranges.map((range) => (
              <button
                key={range}
                type="button"
                onClick={() => void load(range)}
                className={`rounded-md px-3 py-1.5 text-sm ${
                  state.range === range
                    ? 'bg-neutral-700 text-white'
                    : 'text-neutral-400 hover:text-white'
                }`}
              >
                {range}
              </button>
            ))}
          </div>
        </header>

        {isLoading && !stats && (
          <div className="rounded-xl border border-neutral-800 p-8 text-center text-neutral-400">
            Loading dapp analytics…
          </div>
        )}
        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-950/20 p-4 text-sm text-red-200">
            {error}
          </div>
        )}

        {stats && (
          <>
            <div
              className={`rounded-xl border p-4 text-sm ${
                stats.freshness.stale
                  ? 'border-amber-500/30 bg-amber-950/20 text-amber-100'
                  : 'border-neutral-800 bg-neutral-900/60 text-neutral-400'
              }`}
            >
              {stats.freshness.dataThrough
                ? `Data through ${stats.freshness.dataThrough}.`
                : 'No completed analytics run is available yet.'}{' '}
              {stats.freshness.stale &&
                'The pipeline is stale; the last fully succeeded snapshot remains visible.'}
              {stats.freshness.provisionalDates.length > 0 &&
                ` Provisional: ${stats.freshness.provisionalDates.join(', ')}.`}
            </div>

            <section className="rounded-xl border border-neutral-800 bg-neutral-950/70 p-5">
              <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <h2 className="text-base font-semibold text-white">
                    Global Store ranking
                  </h2>
                  {stats.ranking ? (
                    <p className="mt-2 text-sm text-neutral-400">
                      Calculated #{stats.ranking.calculatedRank} · final #
                      {stats.ranking.finalGlobalRank} · weight{' '}
                      {stats.ranking.storeWeight} · score{' '}
                      {stats.ranking.score.toFixed(2)} · {stats.ranking.status}{' '}
                      as of {stats.ranking.asOfDate}
                    </p>
                  ) : (
                    <p className="mt-2 text-sm text-neutral-500">
                      No complete ranking snapshot is available for this app.
                    </p>
                  )}
                </div>
                {stats.ranking && (
                  <div className="flex flex-wrap items-end gap-2">
                    <label className="text-xs text-neutral-400">
                      Android package
                      <input
                        type="text"
                        aria-label="Android package for fixed global rank"
                        value={overridePackage}
                        onChange={(event) =>
                          setOverridePackage(event.target.value)
                        }
                        className="mt-1 block w-64 rounded-md border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm text-white"
                      />
                    </label>
                    <label className="text-xs text-neutral-400">
                      Fixed global rank
                      <input
                        type="number"
                        aria-label="Fixed global rank"
                        min={1}
                        step={1}
                        value={fixedRank}
                        onChange={(event) => setFixedRank(event.target.value)}
                        className="mt-1 block w-32 rounded-md border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm text-white"
                      />
                    </label>
                    <button
                      type="button"
                      disabled={
                        savingRank ||
                        overridePackage.trim().length === 0 ||
                        !Number.isInteger(Number(fixedRank)) ||
                        Number(fixedRank) < 1
                      }
                      onClick={() => void saveFixedRank(Number(fixedRank))}
                      className="rounded-md bg-teal-500 px-3 py-2 text-sm font-medium text-neutral-950 disabled:opacity-40"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      disabled={
                        savingRank || overridePackage.trim().length === 0
                      }
                      onClick={() => void saveFixedRank(null)}
                      className="rounded-md border border-neutral-700 px-3 py-2 text-sm text-neutral-300 disabled:opacity-40"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
              {rankingMessage && (
                <p className="mt-3 text-sm text-neutral-400">
                  {rankingMessage}
                </p>
              )}
            </section>

            {stats.daily.length === 0 ? (
              <div className="rounded-xl border border-neutral-800 p-8 text-center">
                <h2 className="font-medium text-white">No stats yet</h2>
                <p className="mt-2 text-sm text-neutral-500">
                  Stats appear for published apps after the first scheduled
                  refresh.
                </p>
              </div>
            ) : (
              <>
                <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <MetricCard label="Installs" metric={stats.totals.installs} />
                  <MetricCard
                    label="Uninstalls"
                    metric={stats.totals.uninstalls}
                  />
                  <MetricCard
                    label="Net installs"
                    metric={stats.totals.netInstalls}
                  />
                  <MetricCard label="Updates" metric={stats.totals.updates} />
                  <MetricCard
                    label="WAU"
                    metric={stats.activity.wau7}
                    note={
                      stats.activity.wauAsOfDate
                        ? `As of ${stats.activity.wauAsOfDate}`
                        : undefined
                    }
                  />
                  <MetricCard
                    label="MAU"
                    metric={stats.activity.mau30}
                    note={
                      stats.activity.mauAsOfDate
                        ? `Weekly refresh · ${stats.activity.mauAsOfDate}`
                        : 'Refreshes weekly'
                    }
                  />
                  <MetricCard
                    label="Latest version · 7d"
                    metric={stats.activity.latestVersion7dPercent}
                    formatter={formatPercent}
                  />
                  <MetricCard
                    label="Latest version · 30d"
                    metric={stats.activity.latestVersion30dPercent}
                    formatter={formatPercent}
                    note="Refreshes weekly"
                  />
                </section>

                <section className="rounded-xl border border-neutral-800 bg-neutral-950/70 p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h2 className="text-base font-semibold text-white">
                      Daily trend
                    </h2>
                    <div className="flex gap-4 text-xs text-neutral-500">
                      <span>Teal · installs</span>
                      <span>Violet · active devices</span>
                      <span>Faded · provisional</span>
                    </div>
                  </div>
                  <div className="mt-5">
                    <TrendBars stats={stats} />
                  </div>
                </section>

                <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <MetricCard label="Sessions" metric={stats.totals.sessions} />
                  <MetricCard
                    label="Launches"
                    metric={stats.totals.launchCount}
                  />
                  <MetricCard
                    label="Foreground time"
                    metric={stats.totals.foregroundMs}
                    formatter={formatDuration}
                  />
                  <MetricCard
                    label="Average session"
                    metric={stats.totals.averageSessionMs}
                    formatter={formatDuration}
                  />
                </section>

                <div className="grid gap-4 lg:grid-cols-2">
                  <VersionShare
                    title="7-day version share"
                    rows={stats.versionShare7d}
                  />
                  <VersionShare
                    title="30-day version share · weekly"
                    rows={stats.versionShare30d}
                  />
                </div>

                <section className="overflow-hidden rounded-xl border border-neutral-800 bg-neutral-950/70">
                  <div className="border-b border-neutral-800 px-5 py-4">
                    <h2 className="text-base font-semibold text-white">
                      Version adoption by day
                    </h2>
                  </div>
                  {stats.versionDaily.length === 0 ? (
                    <p className="p-5 text-sm text-neutral-500">Unavailable</p>
                  ) : (
                    <div className="max-h-80 overflow-auto">
                      <table className="w-full min-w-[640px] text-left text-sm">
                        <thead className="sticky top-0 bg-neutral-950 text-xs uppercase tracking-wide text-neutral-500">
                          <tr>
                            <th className="px-5 py-3">UTC date</th>
                            <th className="px-3 py-3">Version</th>
                            <th className="px-3 py-3">Installs</th>
                            <th className="px-3 py-3">Updates</th>
                            <th className="px-3 py-3">Active devices</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-800">
                          {stats.versionDaily.map((row) => (
                            <tr key={`${row.date}:${row.versionCode}`}>
                              <td className="px-5 py-3 text-neutral-300">
                                {row.date}
                              </td>
                              <td className="px-3 py-3 text-neutral-300">
                                {row.versionName ?? row.versionCode}
                              </td>
                              <td className="px-3 py-3 tabular-nums">
                                {displayValue(row.installs)}
                              </td>
                              <td className="px-3 py-3 tabular-nums">
                                {displayValue(row.updates)}
                              </td>
                              <td className="px-3 py-3 tabular-nums">
                                {displayValue(row.activeDevices)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>

                <section className="overflow-hidden rounded-xl border border-neutral-800 bg-neutral-950/70">
                  <div className="border-b border-neutral-800 px-5 py-4">
                    <h2 className="text-base font-semibold text-white">
                      Daily details
                    </h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px] text-left text-sm">
                      <thead className="text-xs uppercase tracking-wide text-neutral-500">
                        <tr>
                          <th className="px-5 py-3">UTC date</th>
                          <th className="px-3 py-3">Installs</th>
                          <th className="px-3 py-3">Uninstalls</th>
                          <th className="px-3 py-3">Net</th>
                          <th className="px-3 py-3">Updates</th>
                          <th className="px-3 py-3">DAU</th>
                          <th className="px-3 py-3">Sessions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-800">
                        {stats.daily.map((day) => (
                          <tr
                            key={day.date}
                            className={day.provisional ? 'bg-amber-950/10' : ''}
                          >
                            <td className="px-5 py-3 text-neutral-300">
                              {day.date}
                              {day.provisional && (
                                <span className="ml-2 text-xs text-amber-400">
                                  provisional
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-3 tabular-nums">
                              {displayValue(day.installs)}
                            </td>
                            <td className="px-3 py-3 tabular-nums">
                              {displayValue(day.uninstalls)}
                            </td>
                            <td className="px-3 py-3 tabular-nums">
                              {displayValue(day.netInstalls)}
                            </td>
                            <td className="px-3 py-3 tabular-nums">
                              {displayValue(day.updates)}
                            </td>
                            <td className="px-3 py-3 tabular-nums">
                              {displayValue(day.activeDevices)}
                            </td>
                            <td className="px-3 py-3 tabular-nums">
                              {displayValue(day.sessions)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </>
            )}

            <section className="rounded-xl border border-neutral-800 bg-neutral-950/70 p-5">
              <h2 className="text-base font-semibold text-white">
                Ratings snapshot
              </h2>
              {stats.ratings.available ? (
                <div className="mt-3 space-y-3 text-sm text-neutral-300">
                  <div className="flex flex-wrap gap-6">
                    <span>{stats.ratings.reviews} reviews</span>
                    <span>
                      {stats.ratings.averageReviewScore?.toFixed(2) ??
                        'No average rating'}
                    </span>
                    <span className="text-neutral-500">
                      Persisted{' '}
                      {stats.ratings.fetchedAt ?? 'at an unknown time'}
                    </span>
                  </div>
                  {stats.ratings.reviewsByRating && (
                    <div className="flex flex-wrap gap-4 text-xs text-neutral-500">
                      {stats.ratings.reviewsByRating.map((count, index) => (
                        <span key={index}>
                          {index + 1} star · {count}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="mt-3 text-sm text-neutral-500">
                  No persisted dApp Store ratings snapshot is available.
                </p>
              )}
            </section>

            <section className="rounded-xl border border-neutral-800 bg-neutral-900/40 p-5">
              <h2 className="text-sm font-semibold text-white">
                Population and data notes
              </h2>
              <ul className="mt-3 space-y-2 text-sm leading-6 text-neutral-400">
                {stats.populationNotes.map((note) => (
                  <li key={note.code}>{note.text}</li>
                ))}
              </ul>
            </section>
          </>
        )}
      </div>
    </main>
  );
}
